import { ScanResult, ScanSummary, createScanSummary } from './types';

export function runBrandingScan(): ScanSummary {
  console.group('🏷️ Branding Scan (No NYT References)');
  
  const results: ScanResult[] = [];

  try {
    // Define forbidden terms
    const forbiddenTerms = [
      'wordle',
      'nyt',
      'new york times',
      'wormle', // typo
    ];

    // Check 1: Page content
    const bodyText = document.body.innerText.toLowerCase();
    const foundTerms: string[] = [];

    forbiddenTerms.forEach(term => {
      if (bodyText.includes(term)) {
        foundTerms.push(term);
      }
    });

    results.push({
      name: 'Page Content Check',
      status: foundTerms.length === 0 ? 'pass' : 'fail',
      message: foundTerms.length === 0
        ? 'No forbidden branding terms found in page content'
        : `Found ${foundTerms.length} forbidden term(s): ${foundTerms.join(', ')}`,
      details: 'Checked visible page text for competitor references'
    });

    // Check 2: Meta tags
    const metaTags = document.querySelectorAll('meta');
    const metaIssues: string[] = [];

    metaTags.forEach(meta => {
      const content = meta.getAttribute('content')?.toLowerCase() || '';
      const name = meta.getAttribute('name')?.toLowerCase() || '';
      const property = meta.getAttribute('property')?.toLowerCase() || '';

      forbiddenTerms.forEach(term => {
        if (content.includes(term) || name.includes(term) || property.includes(term)) {
          metaIssues.push(`<meta> contains "${term}"`);
        }
      });
    });

    results.push({
      name: 'Meta Tags Check',
      status: metaIssues.length === 0 ? 'pass' : 'fail',
      message: metaIssues.length === 0
        ? 'No forbidden terms in meta tags'
        : `Found ${metaIssues.length} meta tag issue(s)`,
      details: metaIssues.join(', ') || 'All meta tags clean'
    });

    // Check 3: Alt text and aria-labels
    const images = document.querySelectorAll('img');
    const altIssues: string[] = [];

    images.forEach(img => {
      const alt = img.getAttribute('alt')?.toLowerCase() || '';
      const ariaLabel = img.getAttribute('aria-label')?.toLowerCase() || '';

      forbiddenTerms.forEach(term => {
        if (alt.includes(term) || ariaLabel.includes(term)) {
          altIssues.push(`Image alt/aria-label contains "${term}"`);
        }
      });
    });

    results.push({
      name: 'Image Alt Text Check',
      status: altIssues.length === 0 ? 'pass' : 'fail',
      message: altIssues.length === 0
        ? 'No forbidden terms in image alt text or aria-labels'
        : `Found ${altIssues.length} image issue(s)`,
      details: altIssues.join(', ') || 'All image alt text clean'
    });

    // Check 4: Correct branding usage
    const correctBranding = bodyText.includes('wormble');
    results.push({
      name: 'Wormble Branding',
      status: correctBranding ? 'pass' : 'warn',
      message: correctBranding
        ? 'Wormble branding detected on page'
        : 'Wormble branding not prominently displayed',
      details: 'Verified correct brand name usage'
    });

    // Check 5: Logo check
    const logoElements = document.querySelectorAll('[class*="logo" i], [alt*="logo" i], [aria-label*="logo" i]');
    results.push({
      name: 'Logo Elements',
      status: logoElements.length > 0 ? 'pass' : 'info',
      message: `Found ${logoElements.length} logo element(s)`,
      details: 'Logo elements detected on page'
    });

  } catch (error) {
    results.push({
      name: 'Branding Scan Error',
      status: 'fail',
      message: `Failed to run branding scan: ${error}`,
      details: String(error)
    });
  }

  const summary = createScanSummary('Branding Scan', results);
  
  console.log(`✅ Passed: ${summary.passCount}`);
  console.log(`⚠️  Warnings: ${summary.warnCount}`);
  console.log(`❌ Failed: ${summary.failCount}`);
  console.table(results);
  console.groupEnd();
  
  return summary;
}
